import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { EKpiGoalsStatusBadge } from '@cta-analytics-dialog/cta-analytics-dialog-content/cta-analytics-table/cta-analytics-details/cta-analytics-details-kpi-goals-table/cta-analytics-details-kpi-goals-table.interface';
import { EAnalyticsStatus, EMatchingType } from '@enums/campaign-analytics.enum';
import { ICreatorCtaKpiStatusApi } from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode } from '@interfaces/campaign-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { CircularGraphComponent } from '@shared/components/circular-graph/circular-graph.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { TTileContentStatus } from '@shared/components/social-content-tile/social-content-tile.interface';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { TrackInfoHoverDirective } from '@shared/directives/track-info-hover.directive';
import { toEventFrontendTab } from '@utilities/event-payload-mapping.util';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { TDeepLinkEvent } from '../campaign-details-deep-link/campaign-details-deep-link.interface';
import { CampaignDetailsDeepLinkService } from '../campaign-details-deep-link/campaign-details-deep-link.service';
import { ECampaignDetailsTab, ICampaignDetailsOverviewData } from '../campaign-details.interface';
import { ICampaignExportConfig } from '../campaign-export-config/campaign-export-config.interface';
import { TCampaignHealthStatus } from './campaign-details-overview.interface';
import { CampaignDetailsOverviewService } from './campaign-details-overview.service';
import { ContentStatusBarGraphComponent } from './content-status-bar-graph/content-status-bar-graph.component';
import { IContentStatusBarItem } from './content-status-bar-graph/content-status-bar-graph.interface';

@Component({
  imports: [
    CircularGraphComponent,
    ContentStatusBarGraphComponent,
    NgTemplateOutlet,
    NgxSkeletonLoaderModule,
    StatusBadgeComponent,
    TranslateModule,
    IconComponent,
    MatTooltip,
    NgClass,
    TrackInfoHoverDirective,
  ],
  providers: [CampaignDetailsDeepLinkService],
  selector: 'app-campaign-details-overview',
  templateUrl: './campaign-details-overview.component.html',
})
export class CampaignDetailsOverviewComponent {
  public readonly ctaKpiStatusData = input<ICreatorCtaKpiStatusApi[] | null>(null);
  public readonly analyticsNodes = input<IAnalyticsNode[] | null>(null);
  public readonly loading = input<boolean>(true);
  public readonly hasCtaKpiStatus = input<boolean>(false);
  public readonly campaignExportConfig = input.required<ICampaignExportConfig>();

  public readonly deepLinkClicked = output<TDeepLinkEvent>();

  public readonly EKpiGoalsStatusBadge = EKpiGoalsStatusBadge;
  protected readonly currentTab = toEventFrontendTab(ECampaignDetailsTab.overview);

  public readonly overviewData = computed<ICampaignDetailsOverviewData | null>(() =>
    this.overviewService.buildOverviewData(this.ctaKpiStatusData()),
  );

  private readonly deepLinkService = inject(CampaignDetailsDeepLinkService);
  private readonly overviewService = inject(CampaignDetailsOverviewService);

  // ── Analytics-node fallback counts ──────────────────────────────────────
  private readonly nodeMatchingCounts = computed(() => {
    const nodes = this.analyticsNodes();

    if (!nodes) {
      return { bonus: 0, matched: 0, missing: 0, planned: 0 };
    }

    return nodes.reduce(
      (acc, node) => {
        switch (node.executionStatus) {
          case EMatchingType.planned:
            acc.planned++;
            break;
          case EMatchingType.missing:
            acc.missing++;
            break;
          case EMatchingType.matched:
            acc.matched++;
            break;
          case EMatchingType.bonus:
            acc.bonus++;
            break;
        }

        return acc;
      },
      { bonus: 0, matched: 0, missing: 0, planned: 0 },
    );
  });

  // ── Content Status (Campaign Health) ────────────────────────────────────
  public readonly missingContentsCount = computed((): number => {
    if (this.hasCtaKpiStatus()) {
      return this.overviewData()?.ctaKpiStatus.currentMissingContents ?? 0;
    }

    return this.nodeMatchingCounts().missing;
  });

  public readonly failedContentsCount = computed(
    (): number => this.overviewData()?.ctaKpiStatus?.currentFailedContents ?? 0,
  );

  public readonly problemContentsCount = computed(
    (): number => this.overviewData()?.ctaKpiStatus?.currentProblemContents ?? 0,
  );

  public readonly contentHealthStatus = computed((): TCampaignHealthStatus => {
    if (this.missingContentsCount() > 0 || this.failedContentsCount() > 0 || this.problemContentsCount() > 0) {
      return 'atRisk';
    }

    if (!this.hasCtaKpiStatus()) {
      return 'notStarted';
    }

    return 'healthy';
  });

  // ── KPI Goal Status (Campaign Health) ───────────────────────────────────
  public readonly kpiGoalHealthStatus = computed((): EKpiGoalsStatusBadge => {
    if (!this.hasCtaKpiStatus()) {
      return EKpiGoalsStatusBadge.kpiGoalsNotStarted;
    }

    const kpi = this.overviewData()?.ctaKpiStatus;

    if (!kpi?.expectedKpis) {
      return EKpiGoalsStatusBadge.kpiGoalsNotSet;
    }

    if (!kpi || kpi.currentFailedKpis > 0) {
      return EKpiGoalsStatusBadge.kpiGoalsUnavailable;
    }

    if (kpi.currentProblemKpis > 0) {
      return EKpiGoalsStatusBadge.kpiGoalsBelowTarget;
    }

    return EKpiGoalsStatusBadge.kpiGoalsHealthy;
  });

  public readonly goalsBelowTargetCount = computed(
    (): number => this.overviewData()?.ctaKpiStatus?.currentProblemKpis ?? 0,
  );
  public readonly goalsUnavailableCount = computed(
    (): number => this.overviewData()?.ctaKpiStatus?.currentFailedKpis ?? 0,
  );

  // ── Content Types circular graph ─────────────────────────────────────────
  public readonly contentTypesTotal = computed((): number => {
    if (this.hasCtaKpiStatus()) {
      const kpi = this.overviewData()?.ctaKpiStatus;

      return (kpi?.expectedContents ?? 0) + (kpi?.currentBonusContents ?? 0);
    }

    const counts = this.nodeMatchingCounts();

    return counts.planned + counts.missing + counts.matched + counts.bonus;
  });

  public readonly contentTypesProgressTotal = computed((): number => {
    if (this.hasCtaKpiStatus()) {
      const kpi = this.overviewData()?.ctaKpiStatus;

      return kpi?.expectedContents ?? 0;
    }

    const counts = this.nodeMatchingCounts();

    return counts.planned + counts.missing + counts.matched;
  });

  public readonly contentTypesData = computed(() => {
    if (this.hasCtaKpiStatus()) {
      const kpi = this.overviewData()?.ctaKpiStatus;

      const planned = Math.max(
        0,
        (kpi?.expectedContents ?? 0) - (kpi?.currentMatchedContents ?? 0) - (kpi?.currentMissingContents ?? 0),
      );

      return {
        bonus: kpi?.currentBonusContents ?? 0,
        failed: kpi?.currentMissingContents ?? 0,
        processing: planned,
        success: kpi?.currentMatchedContents ?? 0,
        total: this.contentTypesTotal(),
      };
    }

    const counts = this.nodeMatchingCounts();

    return {
      bonus: counts.bonus,
      failed: counts.missing,
      processing: counts.planned,
      success: counts.matched,
      total: this.contentTypesTotal(),
    };
  });

  public readonly contentTypesMatchedCount = computed((): number => {
    if (this.hasCtaKpiStatus()) {
      return this.overviewData()?.ctaKpiStatus.currentMatchedContents ?? 0;
    }

    return this.nodeMatchingCounts().matched;
  });

  // ── Matched Content Status bar items ─────────────────────────────────────
  public readonly matchedContentStatusItems = computed((): IContentStatusBarItem[] => {
    const kpi = this.overviewData()?.ctaKpiStatus;
    const failed = kpi?.currentFailedContents ?? 0;
    const problem = kpi?.currentProblemContents ?? 0;
    const ok = kpi?.currentOkContents ?? 0;

    return [
      {
        colorClass: 'bg-shikenso-error-bright',
        count: failed,
        key: 'notFulfilled',
        label: 'shared.statuses.notFulfilled',
      },
      {
        colorClass: 'bg-shikenso-warning-base',
        count: problem,
        key: 'partiallyFulfilled',
        label: 'shared.statuses.partiallyFulfilled',
      },
      {
        colorClass: 'bg-shikenso-success-bright',
        count: ok,
        key: 'fulfilled',
        label: 'shared.statuses.fulfilled',
      },
    ];
  });

  private static readonly CONTENT_STATUS_API_TO_TILE: Partial<Record<EAnalyticsStatus, string>> = {
    [EAnalyticsStatus.failed]: 'notFulfilled',
    [EAnalyticsStatus.problem]: 'partiallyFulfilled',
    [EAnalyticsStatus.ok]: 'fulfilled',
  };

  public readonly visibleMatchedContentStatusItems = computed((): IContentStatusBarItem[] => {
    const allowed = this.campaignExportConfig().allowedContentStatuses;
    const items = this.matchedContentStatusItems();

    if (allowed === null) return items;

    const allowedKeys = new Set(allowed.map((s) => CampaignDetailsOverviewComponent.CONTENT_STATUS_API_TO_TILE[s]));

    return items.filter((item) => allowedKeys.has(item.key));
  });

  public readonly matchedContentTotal = computed((): number => {
    const kpi = this.overviewData()?.ctaKpiStatus;

    return (kpi?.currentFailedContents ?? 0) + (kpi?.currentProblemContents ?? 0) + (kpi?.currentOkContents ?? 0);
  });

  // ── Deliverables circular graph ───────────────────────────────────────────
  public readonly campaignExpectedDeliverables = computed<number | null>(
    () => this.overviewData()?.ctaKpiStatus.expectedDeliverables ?? null,
  );

  public readonly deliverablesData = computed(() => {
    const kpi = this.overviewData()?.ctaKpiStatus;

    return {
      failed: kpi?.currentFailedDeliverables ?? 0,
      problem: kpi?.currentProblemDeliverables ?? 0,
      processing: kpi?.currentProcessingDeliverables ?? 0,
      success: kpi?.currentOkDeliverables ?? 0,
      total: this.campaignExpectedDeliverables(),
    };
  });

  // ── KPI Goal Performance circular graph ──────────────────────────────────
  public readonly campaignExpectedKpis = computed<number | null>(
    () => this.overviewData()?.ctaKpiStatus.expectedKpis ?? null,
  );

  public readonly kpiGoalData = computed(() => {
    const kpi = this.overviewData()?.ctaKpiStatus;

    return {
      failed: kpi?.currentFailedKpis ?? 0,
      problem: kpi?.currentProblemKpis ?? 0,
      success: kpi?.currentOkKpis ?? 0,
      total: this.campaignExpectedKpis(),
    };
  });

  // ── Deep-link click handlers ─────────────────────────────────────────────

  public onMissingContentsClicked(): void {
    this.deepLinkClicked.emit({
      matchingTypes: new Set([EMatchingType.missing]),
      tab: ECampaignDetailsTab.contents,
    });
  }

  public onFailedContentsClicked(): void {
    this.deepLinkClicked.emit({
      contentStatuses: new Set<TTileContentStatus>(['notFulfilled']),
      matchingTypes: new Set([EMatchingType.matched]),
      tab: ECampaignDetailsTab.contents,
    });
  }

  public onProblemContentsClicked(): void {
    this.deepLinkClicked.emit({
      contentStatuses: new Set<TTileContentStatus>(['partiallyFulfilled']),
      matchingTypes: new Set([EMatchingType.matched]),
      tab: ECampaignDetailsTab.contents,
    });
  }

  public onGoalsBelowTargetClicked(): void {
    this.deepLinkClicked.emit({
      statuses: new Set([EKpiGoalsStatusBadge.kpiGoalsBelowTarget]),
      tab: ECampaignDetailsTab.kpiGoals,
    });
  }

  public onGoalsUnavailableClicked(): void {
    this.deepLinkClicked.emit({
      statuses: new Set([EKpiGoalsStatusBadge.kpiGoalsUnavailable]),
      tab: ECampaignDetailsTab.kpiGoals,
    });
  }

  public onContentTypeSegmentClicked(segment: string): void {
    const matchingType = this.deepLinkService.contentTypeSegmentMatchingTypeMap[segment];

    if (!matchingType) {
      return;
    }

    this.deepLinkClicked.emit({
      matchingTypes: new Set([matchingType]),
      tab: ECampaignDetailsTab.contents,
    });
  }

  public onContentTypeLegendClicked(segment: string): void {
    this.onContentTypeSegmentClicked(segment);
  }

  public onContentTypesProgressClicked(): void {
    this.deepLinkClicked.emit({
      matchingTypes: new Set([EMatchingType.missing, EMatchingType.matched, EMatchingType.planned]),
      tab: ECampaignDetailsTab.contents,
    });
  }

  public onMatchedContentStatusItemClicked(item: IContentStatusBarItem): void {
    const contentStatus = item.key;

    if (!this.isTileContentStatus(contentStatus)) {
      return;
    }

    this.deepLinkClicked.emit({
      contentStatuses: new Set<TTileContentStatus>([contentStatus]),
      matchingTypes: new Set([EMatchingType.matched]),
      tab: ECampaignDetailsTab.contents,
    });
  }

  public onDeliverableSegmentClicked(segment: string): void {
    const status = this.deepLinkService.deliverableSegmentStatusMap[segment];

    if (!status) {
      return;
    }

    this.deepLinkClicked.emit({
      matchingTypes: new Set([EMatchingType.matched]),
      statuses: new Set([status]),
      tab: ECampaignDetailsTab.deliverables,
    });
  }

  public onDeliverableLegendClicked(segment: string): void {
    this.onDeliverableSegmentClicked(segment);
  }

  public onKpiGoalSegmentClicked(segment: string): void {
    const status = this.deepLinkService.kpiGoalSegmentStatusMap[segment];

    if (!status) {
      return;
    }

    this.deepLinkClicked.emit({
      statuses: new Set([status]),
      tab: ECampaignDetailsTab.kpiGoals,
    });
  }

  public onKpiGoalLegendClicked(segment: string): void {
    this.onKpiGoalSegmentClicked(segment);
  }

  // ── Export mode: content-type legend visibility ───────────────────────────

  public readonly isMissingContentTypeVisible = computed(() => {
    const allowed = this.campaignExportConfig().allowedContentMatchingTypes;

    return allowed === null || allowed.includes(EMatchingType.missing);
  });

  public readonly isMatchedContentTypeVisible = computed(() => {
    const allowed = this.campaignExportConfig().allowedContentMatchingTypes;

    return allowed === null || allowed.includes(EMatchingType.matched);
  });

  public readonly isBonusContentTypeVisible = computed(() => {
    const allowed = this.campaignExportConfig().allowedContentMatchingTypes;

    return allowed === null || allowed.includes(EMatchingType.bonus);
  });

  public readonly isPlannedContentTypeVisible = computed(() => {
    const allowed = this.campaignExportConfig().allowedContentMatchingTypes;

    return allowed === null || allowed.includes(EMatchingType.planned);
  });

  // ── Export mode: deliverable legend visibility ────────────────────────────

  public readonly isDeliverableFailedVisible = computed(() => {
    const allowed = this.campaignExportConfig().allowedDeliverableStatuses;

    return allowed === null || allowed.includes(EAnalyticsStatus.failed);
  });

  public readonly isDeliverableProblemVisible = computed(() => {
    const allowed = this.campaignExportConfig().allowedDeliverableStatuses;

    return allowed === null || allowed.includes(EAnalyticsStatus.problem);
  });

  public readonly isDeliverableOkVisible = computed(() => {
    const allowed = this.campaignExportConfig().allowedDeliverableStatuses;

    return allowed === null || allowed.includes(EAnalyticsStatus.ok);
  });

  public readonly isDeliverableProcessingVisible = computed(() => {
    const allowed = this.campaignExportConfig().allowedDeliverableStatuses;

    return allowed === null || allowed.includes(EAnalyticsStatus.processing);
  });

  // ── Export mode: KPI legend visibility ────────────────────────────────────

  public readonly isKpiFailedVisible = computed(() => {
    const allowed = this.campaignExportConfig().allowedKpiStatuses;

    return allowed === null || allowed.includes(EAnalyticsStatus.failed);
  });

  public readonly isKpiProblemVisible = computed(() => {
    const allowed = this.campaignExportConfig().allowedKpiStatuses;

    return allowed === null || allowed.includes(EAnalyticsStatus.problem);
  });

  public readonly isKpiOkVisible = computed(() => {
    const allowed = this.campaignExportConfig().allowedKpiStatuses;

    return allowed === null || allowed.includes(EAnalyticsStatus.ok);
  });

  private isTileContentStatus(value: string | undefined): value is TTileContentStatus {
    return value === 'fulfilled' || value === 'partiallyFulfilled' || value === 'notFulfilled';
  }
}
